/**
 * ImageUpload — crop-to-square, resize ≤512px, upload to Firebase Storage.
 *
 * Flow:
 *   1. Click area → file input (image/* only).
 *   2. Canvas crop to square + resize to MAX_DIMENSION (512px).
 *   3. Upload via Firebase Storage to per-tenant path.
 *   4. onUpload(url) called; parent PATCH-es photo_url on the entity.
 */

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import app from '@core/infrastructure/firebase';
import { entityPhotoPath } from './storagePaths';

const MAX_DIMENSION = 512;

interface ImageUploadProps {
  currentUrl?: string | null;
  onUpload: (url: string) => void;
  /** Sub-path below entityType, e.g. the entity ID. */
  storagePath: string;
  tenantId: string;
  entityType?: string;
  disabled?: boolean;
}

const Wrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: inline-block;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const PhotoCircle = styled.div<{ $hasImage: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme, $hasImage }) =>
    $hasImage ? 'transparent' : theme.colors.backgroundTertiary};
  border: 2px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${Wrapper}:hover & { opacity: 1; }
`;

const ProgressRing = styled.div<{ $pct: number }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: conic-gradient(
    ${({ theme }) => theme.colors.primary} ${({ $pct }) => $pct}%,
    ${({ theme }) => theme.colors.borderLight} ${({ $pct }) => $pct}%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ErrorMsg = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.error};
  max-width: 80px;
  text-align: center;
`;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = reject;
    img.src = url;
  });
}

async function cropAndResize(file: File): Promise<Blob> {
  const img = await loadImage(file);
  const size = Math.min(img.width, img.height);
  const sx = (img.width - size) / 2;
  const sy = (img.height - size) / 2;
  const dim = Math.min(size, MAX_DIMENSION);
  const canvas = document.createElement('canvas');
  canvas.width = dim;
  canvas.height = dim;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, sx, sy, size, size, 0, 0, dim, dim);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => { if (blob) resolve(blob); else reject(new Error('toBlob returned null')); },
      'image/jpeg',
      0.88,
    );
  });
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  currentUrl,
  onUpload,
  storagePath,
  tenantId,
  entityType = 'misc',
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const storage = getStorage(app);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    setError(null);
    setProgress(0);
    try {
      const blob = await cropAndResize(file);
      setPreview(URL.createObjectURL(blob));
      const storageRef = ref(storage, entityPhotoPath(tenantId, entityType, storagePath));
      const task = uploadBytesResumable(storageRef, blob, { contentType: 'image/jpeg' });
      await new Promise<void>((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          (err) => { setError('Upload failed. Please try again.'); setProgress(null); reject(err); },
          async () => { onUpload(await getDownloadURL(task.snapshot.ref)); setProgress(null); resolve(); },
        );
      });
    } catch {
      setError('Upload failed. Please try again.');
      setProgress(null);
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const displayUrl = preview ?? currentUrl;

  return (
    <div>
      <Wrapper
        $disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        data-testid="candeza-image-upload"
        title="Click to change photo"
      >
        {progress !== null ? (
          <ProgressRing $pct={progress}>{progress}%</ProgressRing>
        ) : (
          <PhotoCircle $hasImage={Boolean(displayUrl)}>
            {displayUrl ? (
              <Photo src={displayUrl} alt="Profile" />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" opacity={0.4}>
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </PhotoCircle>
        )}
        {!disabled && progress === null && <Overlay><span>Change</span></Overlay>}
      </Wrapper>
      {error && <ErrorMsg>{error}</ErrorMsg>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        data-testid="candeza-image-upload-input"
      />
    </div>
  );
};
